import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ArrowLeft, Hash, History, Loader2, Plus, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import { useOnlineUserIds } from '../../hooks/usePresence';
import {
  useOpenDm,
  useReorderConversations,
  useTeamChatConversations,
  useTeamChatSearch,
} from '../../hooks/useTeamChat';
import { useUsers } from '../../hooks/useUsers';
import { useCreateVoiceChannel, useVoiceChannels, useVoicePresence } from '../../hooks/useVoice';
import type { TeamChatSearchResult, TeamConversation } from '../../lib/teamChatApi';
import type { VoiceChannel } from '../../lib/voiceApi';
import { useAuthStore } from '../../store/useAuthStore';
import { Avatar } from '../tasks/linear/Avatar';
import { SidebarHeaderControls } from '../layout/SidebarHeaderControls';
import { usePrompt } from '../ui/ConfirmProvider';
import { Skeleton } from '../ui/Skeleton';
import { CreateGroupModal } from './CreateGroupModal';
import { NewDmModal } from './NewDmModal';
import { AvatarWithPresence } from './PresenceDot';
import { UnreadBadge } from './UnreadBadge';
import { VOICE_CALLS_PATH } from './VoiceCallHistory';
import { VoiceChannelRow, voiceChannelPath } from './VoiceChannelRow';
import { VoiceChannelSettingsModal } from './VoiceChannelSettingsModal';

/** DM display name = the other member (fall back to any member for self-DMs). */
export function conversationTitle(
  conversation: TeamConversation,
  meId: string | undefined,
): string {
  if (conversation.type === 'GROUP') return conversation.name ?? '';
  const peer = conversation.members.find((m) => m.id !== meId) ?? conversation.members[0];
  return peer?.name ?? '';
}

/** Shared by conversation rows and the "start a DM" rows below them. */
const ROW_CLASS =
  'flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors';

function ConversationRow({
  conversation,
  meId,
  online,
  onNavigate,
}: {
  conversation: TeamConversation;
  meId: string | undefined;
  online: boolean;
  onNavigate?: () => void;
}) {
  const title = conversationTitle(conversation, meId);
  return (
    <NavLink
      to={`/team-chat/${conversation.id}`}
      onClick={onNavigate}
      className={({ isActive }) =>
        [
          ROW_CLASS,
          isActive
            ? 'bg-sidebar-accent text-sidebar-accent-foreground'
            : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        ].join(' ')
      }
    >
      {conversation.type === 'GROUP' ? (
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sidebar-accent">
          <Hash size={14} className="text-muted-foreground" />
        </span>
      ) : (
        <AvatarWithPresence online={online}>
          <Avatar name={title || null} size={24} />
        </AvatarWithPresence>
      )}
      {/* Name only, on one line, for channels and DMs alike — like Discord's
          sidebar. The last message used to sit under text-channel names; it
          made the list read as an inbox, gave the rows two different heights,
          and put a snippet of whatever was said last (a pasted URL, as often
          as not) permanently on screen. Unread still shows as the count on the
          right, which is what the row is actually scanned for. */}
      <span className="min-w-0 flex-1 truncate">{title}</span>
      <UnreadBadge count={conversation.unreadCount} />
    </NavLink>
  );
}

/**
 * A group row the user can drag into place. Listeners sit on the wrapper, not a
 * grip, so the whole row drags exactly like Discord; `attributes` are left off
 * deliberately — they'd put `role="button"` around a NavLink, and the keyboard
 * drag they enable has no sensor mounted anyway.
 */
function SortableGroupRow({
  conversation,
  meId,
  onNavigate,
}: {
  conversation: TeamConversation;
  meId: string | undefined;
  onNavigate?: () => void;
}) {
  const { listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: conversation.id,
  });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={isDragging ? 'relative z-10 opacity-60' : ''}
      {...listeners}
    >
      <ConversationRow
        conversation={conversation}
        meId={meId}
        online={false}
        onNavigate={onNavigate}
      />
    </div>
  );
}

/** One message hit: who said it, where, and enough of it to recognise. */
function MessageHitRow({
  result,
  onOpen,
}: {
  result: TeamChatSearchResult;
  onOpen: (conversationId: string, messageId: string) => void;
}) {
  const { locale } = useI18n();
  return (
    <button
      type="button"
      onClick={() => onOpen(result.conversationId, result.messageId)}
      className={`${ROW_CLASS} w-full items-start text-left font-normal text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground`}
    >
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline gap-1.5">
          {result.conversationType === 'GROUP' && <Hash size={12} className="shrink-0" />}
          <span className="truncate text-xs font-semibold">{result.conversationName}</span>
          <span className="shrink-0 text-[10px]">
            {new Date(result.createdAt).toLocaleDateString(locale)}
          </span>
        </span>
        <span className="line-clamp-2 block text-xs">
          {result.authorName}: {result.snippet}
        </span>
      </span>
    </button>
  );
}

/**
 * A colleague you have no DM with yet. One click opens the conversation, so
 * the whole team is reachable from the sidebar without going through search.
 */
function StartDmRow({
  user,
  online,
  onOpen,
  pending,
}: {
  user: { id: string; name: string };
  online: boolean;
  onOpen: (userId: string) => void;
  pending: boolean;
}) {
  return (
    <button
      type="button"
      onClick={() => onOpen(user.id)}
      disabled={pending}
      className={`${ROW_CLASS} w-full text-left font-normal text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground disabled:opacity-50`}
    >
      <AvatarWithPresence online={online}>
        <Avatar name={user.name || null} size={24} />
      </AvatarWithPresence>
      <span className="min-w-0 flex-1 truncate">{user.name}</span>
    </button>
  );
}

/**
 * Replaces the app sidebar while on /team-chat*: Discord's server column, top
 * to bottom — the text channels (admin-managed groups, drag-sortable per user),
 * the voice channels (with whoever is inside listed under each), then direct
 * messages, where EVERY colleague is listed whether or not you have written to
 * them yet. Rooms first, people last: the two channel lists are places in the
 * company and belong together, and the people list is the long one, so it does
 * not push anything off the bottom. The "Voice connected" panel is not here:
 * it lives in the sidebar shell (VoiceSidebarPanel) so it survives the swap to
 * any other sidebar.
 */

/**
 * What the sidebar looks like before its lists have loaded: the three section
 * headings with a few rows each, in the rows' own geometry (24px avatar, one
 * line of text), so nothing shifts when the real thing replaces it.
 */
function SidebarSkeleton() {
  const sections: Array<{ rows: number; round: boolean }> = [
    { rows: 3, round: false },
    { rows: 6, round: true },
    { rows: 2, round: false },
  ];
  return (
    <div className="space-y-4" aria-busy="true">
      {sections.map((section, i) => (
        <div key={i}>
          <div className="px-3 py-1">
            <Skeleton className="h-3 w-24" />
          </div>
          <div className="mt-1 space-y-0.5">
            {Array.from({ length: section.rows }).map((_, j) => (
              <div key={j} className="flex items-center gap-2.5 px-3 py-2">
                <Skeleton
                  className={`h-6 w-6 shrink-0 ${section.round ? 'rounded-full' : 'rounded-md'}`}
                />
                <Skeleton className="h-3.5" width={`${55 + ((i * 7 + j * 13) % 30)}%`} />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function TeamChatSidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const me = useAuthStore((s) => s.user);
  const isAdmin = me?.role === 'ADMIN';
  const { data, isLoading: conversationsLoading } = useTeamChatConversations();
  const { data: users, isLoading: usersLoading } = useUsers();
  const onlineIds = useOnlineUserIds();
  const openDm = useOpenDm();
  const reorder = useReorderConversations();
  // Voice is opt-in infrastructure: with LIVEKIT_* unset the server answers
  // `enabled: false` and the whole section stays out of the sidebar.
  const { data: voiceChannels, isLoading: voiceChannelsLoading } = useVoiceChannels();
  const { data: voice, isLoading: voicePresenceLoading } = useVoicePresence();
  const voiceEnabled = voice?.enabled ?? false;
  // The four lists arrive from four requests. Rendering each as it lands made
  // the sidebar assemble itself in front of the user — text channels, then
  // people, then the voice block sliding in — so until every first load is
  // in, one skeleton stands in for all of them. Later polls never trip this:
  // `isLoading` is only true while there is no data at all.
  const booting =
    conversationsLoading || usersLoading || voiceChannelsLoading || voicePresenceLoading;
  const createVoiceChannel = useCreateVoiceChannel();
  const prompt = usePrompt();
  const [voiceSettingsFor, setVoiceSettingsFor] = useState<VoiceChannel | null>(null);
  const [showNewDm, setShowNewDm] = useState(false);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Conversations and colleagues are already in memory, so those filter as you
  // type. Only the message search goes to the server — hence the pause.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(handle);
  }, [search]);

  const searching = search.trim().length > 0;
  // A single character matches most of the history, so the server refuses it and
  // the section stays out of the way until the query says something.
  const searchingMessages = search.trim().length >= 2;
  const { data: hits, isFetching } = useTeamChatSearch(debouncedSearch);
  const messageHits = hits ?? [];
  // Either the request is in flight, or the debounce hasn't handed the query
  // over yet. Without the second half, "no results" flashes for 250ms on every
  // keystroke — an answer to a question that hasn't been asked.
  const messagesPending = isFetching || debouncedSearch.trim() !== search.trim();

  // A mouse drag needs 8px of travel so a plain click still navigates; touch
  // needs a long press, which leaves a tap as navigation and a swipe as
  // scrolling the sidebar.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } }),
  );

  const conversations = data ?? [];
  const needle = search.trim().toLowerCase();
  const matchesSearch = (c: TeamConversation) =>
    needle === '' || conversationTitle(c, me?.id).toLowerCase().includes(needle);

  // Groups are user-arranged; anything never dragged keeps the server's
  // recency order, below the arranged ones.
  const groups = conversations
    .filter((c) => c.type === 'GROUP')
    .sort((a, b) => {
      if (a.sortOrder === null && b.sortOrder === null) return 0;
      if (a.sortOrder === null) return 1;
      if (b.sortOrder === null) return -1;
      return a.sortOrder - b.sortOrder;
    });
  const groupIds = groups.map((c) => c.id);
  const visibleGroups = groups.filter(matchesSearch);
  /** Whether the peer of a DM is online (groups don't get a dot). */
  const dmPeerOnline = (c: TeamConversation) => {
    const peer = c.members.find((m) => m.id !== me?.id);
    return peer !== undefined && onlineIds.has(peer.id);
  };

  // Direct messages list EVERY colleague, like Discord's member list — not
  // only the people you have already written to. A colleague with a DM shows
  // the conversation (unread badge, last message); one without starts the DM
  // on click. Existing conversations come first, on recency, because they are
  // a feed; the rest follow online-first, then alphabetical. DMs whose peer is
  // no longer an active account are kept at the end so history never vanishes.
  const dmRows = useMemo(() => {
    const dmByPeer = new Map<string, TeamConversation>();
    for (const c of conversations) {
      if (c.type !== 'DM') continue;
      const peer = c.members.find((m) => m.id !== me?.id) ?? c.members[0];
      if (peer) dmByPeer.set(peer.id, c);
    }
    const colleagues = (users ?? []).filter((u) => u.id !== me?.id);
    const withDm = colleagues
      .filter((u) => dmByPeer.has(u.id))
      .map((u) => ({ user: u, conversation: dmByPeer.get(u.id)! }))
      .sort((a, b) =>
        (b.conversation.lastMessageAt ?? '').localeCompare(a.conversation.lastMessageAt ?? ''),
      );
    const withoutDm = colleagues
      .filter((u) => !dmByPeer.has(u.id))
      .map((u) => ({ user: u, conversation: null }))
      .sort((a, b) => {
        const byPresence = Number(onlineIds.has(b.user.id)) - Number(onlineIds.has(a.user.id));
        return byPresence !== 0 ? byPresence : a.user.name.localeCompare(b.user.name);
      });
    const known = new Set(colleagues.map((u) => u.id));
    const orphaned = [...dmByPeer.entries()]
      .filter(([peerId]) => !known.has(peerId))
      .map(([, conversation]) => ({ user: null, conversation }));
    return [...withDm, ...withoutDm, ...orphaned].filter(({ user, conversation }) => {
      if (needle === '') return true;
      const name = user?.name ?? (conversation ? conversationTitle(conversation, me?.id) : '');
      return name.toLowerCase().includes(needle);
    });
  }, [users, conversations, me?.id, onlineIds, needle]);

  const visibleVoiceChannels = (voiceChannels ?? []).filter(
    (ch) => needle === '' || ch.name.toLowerCase().includes(needle),
  );

  const newVoiceChannel = async () => {
    const name = await prompt({
      title: t('teamChat.newVoiceChannel'),
      message: t('teamChat.voiceChannelName'),
      confirmLabel: t('common.create'),
    });
    if (!name?.trim()) return;
    createVoiceChannel.mutate(name.trim(), {
      onSuccess: (channel) => {
        navigate(voiceChannelPath(channel.id));
        onNavigate?.();
      },
    });
  };

  const onGroupDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = groupIds.indexOf(String(active.id));
    const to = groupIds.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    reorder.mutate(arrayMove(groupIds, from, to));
  };

  const startDm = (userId: string) => {
    openDm.mutate(userId, {
      onSuccess: (conversation) => {
        navigate(`/team-chat/${conversation.id}`);
        onNavigate?.();
      },
    });
  };

  // `?m=` is read by ConversationView, which pages the history back until the
  // message is mounted and then flashes it.
  const openMessageHit = (conversationId: string, messageId: string) => {
    setSearch('');
    navigate(`/team-chat/${conversationId}?m=${encodeURIComponent(messageId)}`);
    onNavigate?.();
  };

  return (
    <nav className="flex h-full flex-col overflow-y-auto pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)]">
      <div className="flex items-center justify-between px-4 py-4 pt-[calc(1rem+env(safe-area-inset-top))]">
        <Link
          to="/dashboard"
          onClick={onNavigate}
          className="flex items-center gap-2 text-sm font-bold text-sidebar-foreground hover:text-foreground"
        >
          <ArrowLeft size={16} />
          {t('teamChat.backToApp')}
        </Link>
        <SidebarHeaderControls />
      </div>

      <div className="px-3 pb-1">
        <div className="relative">
          <Search
            size={14}
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('teamChat.searchPlaceholder')}
            className="w-full rounded-md border border-sidebar-border bg-background py-1.5 pl-8 pr-8 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-primary"
          />
          {searching && (
            <button
              type="button"
              onClick={() => setSearch('')}
              aria-label={t('teamChat.searchClear')}
              title={t('teamChat.searchClear')}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:bg-sidebar-accent hover:text-foreground"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 space-y-4 px-3 pb-6">
        {booting ? (
          <SidebarSkeleton />
        ) : (
          <>
            {/* Text channels — group chats, created and managed by admins only. */}
            <div>
              <div className="flex items-center justify-between px-3 py-1">
                <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  {t('teamChat.textChannels')}
                </span>
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => setShowNewGroup(true)}
                    aria-label={t('teamChat.newGroup')}
                    title={t('teamChat.newGroup')}
                    className="rounded p-0.5 text-muted-foreground hover:bg-sidebar-accent hover:text-foreground"
                  >
                    <Plus size={16} />
                  </button>
                )}
              </div>
              <div className="mt-1 space-y-0.5">
                {/* Dragging is off while filtering: a reorder of the *visible* rows
                would persist an order computed from a subset, scrambling the
                groups the filter is hiding. */}
                {searching ? (
                  visibleGroups.map((c) => (
                    <ConversationRow
                      key={c.id}
                      conversation={c}
                      meId={me?.id}
                      online={false}
                      onNavigate={onNavigate}
                    />
                  ))
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={onGroupDragEnd}
                  >
                    <SortableContext items={groupIds} strategy={verticalListSortingStrategy}>
                      {groups.map((c) => (
                        <SortableGroupRow
                          key={c.id}
                          conversation={c}
                          meId={me?.id}
                          onNavigate={onNavigate}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                )}
                {visibleGroups.length === 0 && (
                  <p className="px-3 py-1.5 text-sm text-muted-foreground">
                    {searching ? t('teamChat.searchNoResults') : t('teamChat.noGroups')}
                  </p>
                )}
              </div>
            </div>

            {/* Direct messages — anyone can DM anyone. */}
            <div>
              <div className="flex items-center justify-between px-3 py-1">
                <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                  {t('teamChat.directMessages')}
                </span>
                <button
                  type="button"
                  onClick={() => setShowNewDm(true)}
                  aria-label={t('teamChat.newDm')}
                  title={t('teamChat.newDm')}
                  className="rounded p-0.5 text-muted-foreground hover:bg-sidebar-accent hover:text-foreground"
                >
                  <Plus size={16} />
                </button>
              </div>
              <div className="mt-1 space-y-0.5">
                {dmRows.map(({ user, conversation }) =>
                  conversation ? (
                    <ConversationRow
                      key={conversation.id}
                      conversation={conversation}
                      meId={me?.id}
                      online={dmPeerOnline(conversation)}
                      onNavigate={onNavigate}
                    />
                  ) : (
                    <StartDmRow
                      key={user!.id}
                      user={user!}
                      online={onlineIds.has(user!.id)}
                      onOpen={startDm}
                      pending={openDm.isPending}
                    />
                  ),
                )}
                {dmRows.length === 0 && (
                  <p className="px-3 py-1.5 text-sm text-muted-foreground">
                    {searching ? t('teamChat.searchNoResults') : t('teamChat.noPeople')}
                  </p>
                )}
              </div>
            </div>

            {/* Voice channels — company-wide rooms you walk into, last in the
            list (Andrei, 2026-09-29): text channels and people are what you
            open all day, rooms are the occasional stop. Created and managed by
            admins only. */}
            {voiceEnabled && (
              <div>
                <div className="flex items-center justify-between px-3 py-1">
                  <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                    {t('teamChat.voiceChannels')}
                  </span>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => void newVoiceChannel()}
                      disabled={createVoiceChannel.isPending}
                      aria-label={t('teamChat.newVoiceChannel')}
                      title={t('teamChat.newVoiceChannel')}
                      className="rounded p-0.5 text-muted-foreground hover:bg-sidebar-accent hover:text-foreground disabled:opacity-50"
                    >
                      <Plus size={16} />
                    </button>
                  )}
                </div>
                <div className="mt-1 space-y-0.5">
                  {visibleVoiceChannels.map((ch) => (
                    <VoiceChannelRow
                      key={ch.id}
                      channel={ch}
                      participants={voice?.channels[ch.id] ?? []}
                      canManage={isAdmin}
                      onSettings={() => setVoiceSettingsFor(ch)}
                      onNavigate={onNavigate}
                    />
                  ))}
                  {visibleVoiceChannels.length === 0 && (
                    <p className="px-3 py-1.5 text-sm text-muted-foreground">
                      {searching ? t('teamChat.searchNoResults') : t('teamChat.noVoiceChannels')}
                    </p>
                  )}
                  {/* What the channels remember: every call, with its transcript.
                      Hidden while filtering — it is not a channel. */}
                  {!searching && (
                    <NavLink
                      to={VOICE_CALLS_PATH}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        [
                          'flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors',
                          isActive
                            ? 'bg-sidebar-accent font-medium text-foreground'
                            : 'text-sidebar-foreground hover:bg-sidebar-accent hover:text-foreground',
                        ].join(' ')
                      }
                    >
                      <History size={16} className="shrink-0 text-muted-foreground" />
                      <span className="truncate">{t('teamChat.voiceCallHistory')}</span>
                    </NavLink>
                  )}
                </div>
              </div>
            )}

            {/* Message hits — the only section that needs the server, so it's also
            the only one that can lag behind what's in the box. */}
            {searchingMessages && (
              <div>
                <div className="flex items-center gap-1.5 px-3 py-1">
                  <span className="text-xs font-bold uppercase tracking-wide text-muted-foreground">
                    {t('teamChat.searchMessages')}
                  </span>
                  {messagesPending && (
                    <Loader2 size={12} className="animate-spin text-muted-foreground" />
                  )}
                </div>
                <div className="mt-1 space-y-0.5">
                  {messageHits.map((hit) => (
                    <MessageHitRow key={hit.messageId} result={hit} onOpen={openMessageHit} />
                  ))}
                  {messageHits.length === 0 && !messagesPending && (
                    <p className="px-3 py-1.5 text-sm text-muted-foreground">
                      {t('teamChat.searchNoResults')}
                    </p>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <NewDmModal
        open={showNewDm}
        onClose={() => setShowNewDm(false)}
        onOpened={(conversationId) => {
          setShowNewDm(false);
          navigate(`/team-chat/${conversationId}`);
          onNavigate?.();
        }}
      />
      {isAdmin && voiceSettingsFor && (
        <VoiceChannelSettingsModal
          channel={voiceSettingsFor}
          onClose={() => setVoiceSettingsFor(null)}
          onDeleted={() => {
            // The page for a deleted channel would only say "not found".
            if (pathname === voiceChannelPath(voiceSettingsFor.id)) navigate('/team-chat');
          }}
        />
      )}
      {isAdmin && (
        <CreateGroupModal
          open={showNewGroup}
          onClose={() => setShowNewGroup(false)}
          onCreated={(conversationId) => {
            setShowNewGroup(false);
            navigate(`/team-chat/${conversationId}`);
            onNavigate?.();
          }}
        />
      )}
    </nav>
  );
}
